import { jwtDecode } from 'jwt-decode';
import DeviceInfo from 'react-native-device-info';
import {
  appActive,
  appOpened,
  appInBackground,
  startListening,
  appSync,
  appLogout,
  isAnyOf,
  registerUser,
  initiateLogin,
} from '@betty/toolkit/state';
import { apiConfig } from '@betty/toolkit/api';
import { SecureStorage } from '@betty/toolkit/storage';
import { logError, logInfo } from '@betty/toolkit/logging';
import { navigationRef } from '@betty/navigation';
import {
  clearMFAResendCodeTimeout,
  getToken,
  login,
  logout,
  refreshToken,
  resendMFACode,
  resetMFAResendCodeTimeout,
  selectIsLogged,
  selectMFAResendCodeTimeout,
  selectRefreshTokenDataState,
  setLoggedIn,
  setRefreshTokenDataState,
  validateMFA,
} from './auth.slice';
import AuthService from '../../services/AuthService';
import {
  setAutoLoginFulfilled,
  setAutoLoginPending,
  setAutoLoginRejected,
} from '@betty/registration/state';

startListening({
  actionCreator: initiateLogin,
  effect: () => {
    navigationRef.navigate('LoginScreen');
  },
});

startListening({
  matcher: isAnyOf(login.fulfilled, validateMFA.fulfilled),
  effect: async (_, { dispatch }) => {
    dispatch(appSync());

    const userId = await apiConfig.getUserId();

    if (userId) {
      AuthService.loginCompleted({ pathParams: { userId } });
    }
  },
});

startListening({
  actionCreator: login.rejected,
  effect: (action, { dispatch }) => {
    if (action.payload && 'requireTwoFactorVerification' in action.payload) {
      dispatch(resetMFAResendCodeTimeout());
    }
  },
});

startListening({
  actionCreator: resendMFACode.fulfilled,
  effect: (_, { dispatch }) => {
    dispatch(resetMFAResendCodeTimeout());
  },
});

startListening({
  actionCreator: resetMFAResendCodeTimeout,
  effect: async (_, { getState, dispatch, cancelActiveListeners, delay }) => {
    cancelActiveListeners();

    const state = getState();
    const timeout = selectMFAResendCodeTimeout(state);

    if (!timeout) {
      return;
    }

    const timeLeft = Math.max(timeout - +new Date(), 0);

    await delay(timeLeft);

    dispatch(clearMFAResendCodeTimeout());
  },
});

startListening({
  matcher: isAnyOf(
    appOpened,
    appActive,
    refreshToken.fulfilled,
    login.fulfilled,
    validateMFA.fulfilled,
  ),
  effect: async (_, { dispatch, cancelActiveListeners, delay, take }) => {
    cancelActiveListeners();

    const token = await apiConfig.getToken();

    if (token) {
      const idToken = jwtDecode(token.access_token);
      const expirySeconds = idToken.exp || 0;
      const timeLeft = expirySeconds * 1000 - +new Date();
      const timeout = Math.max(timeLeft - 10000, 0);

      logInfo('auth', 'SETTING REFRESH TIMER FOR SECONDS: ', timeout / 1000);

      const result = await Promise.race([
        delay(timeout),
        take(appInBackground.match),
      ]);

      if (Array.isArray(result) && result[0]?.type === appInBackground.type) {
        logInfo(
          'auth',
          'Refresh token timer cancelled due to app going to background',
        );
        return;
      }

      await delay(timeout);

      dispatch(refreshToken());
    }
  },
});

startListening({
  actionCreator: appInBackground,
  effect: (_, { getState, dispatch }) => {
    const isLogged = selectIsLogged(getState());

    if (isLogged) {
      dispatch(setRefreshTokenDataState('stale'));
    }
  },
});

startListening({
  matcher: isAnyOf(appOpened, appActive),
  effect: async (action, { dispatch, getState }) => {
    const state = getState();
    const isLogged = selectIsLogged(state);
    const refreshTokenDataState = selectRefreshTokenDataState(state);
    const isLoggedInOnOpen = action.type === appOpened.type && isLogged;

    if (isLoggedInOnOpen || refreshTokenDataState === 'stale') {
      const result = await dispatch(refreshToken());

      if (refreshToken.fulfilled.match(result)) {
        dispatch(appSync());
      }
    }
  },
});

startListening({
  actionCreator: registerUser,
  effect: async (action, { dispatch }) => {
    const { codeVerifier, authorizationCode } = action.payload;
    dispatch(setAutoLoginPending());

    try {
      await getToken({
        grant_type: 'authorization_code',
        redirect_uri: 'bettyapp://callback',
        code: authorizationCode,
        code_verifier: codeVerifier,
      });
      dispatch(setLoggedIn(true));
      dispatch(appSync());

      const userId = await apiConfig.getUserId();
      if (userId) {
        await AuthService.loginCompleted({ pathParams: { userId } });
      }
      dispatch(setAutoLoginFulfilled());
    } catch (error) {
      logError('Registration token exchange failed:', error);
      dispatch(setAutoLoginRejected());
    }
  },
});

startListening({
  actionCreator: logout.fulfilled,
  effect: (_, { dispatch }) => {
    dispatch(appLogout());
  },
});

startListening({
  actionCreator: refreshToken.rejected,
  effect: (_, { dispatch }) => {
    dispatch(logout());

    SecureStorage.removeItem('token');
    SecureStorage.removeItem('access_token');
    SecureStorage.removeItem('user_id');
    SecureStorage.removeItem('refresh_token');
  },
});

let heartBeatInterval: number | undefined;

const clearHeartBeatInterval = () => {
  clearInterval(heartBeatInterval);
  heartBeatInterval = undefined;
};

startListening({
  actionCreator: appSync,
  effect: () => {
    clearHeartBeatInterval();

    heartBeatInterval = window.setInterval(async () => {
      logInfo('Activity', 'check');

      const data = {
        playerId: await apiConfig.getUserId(),
        pageUrl: navigationRef.getCurrentRoute()?.name || 'Home',
        userAgent: await DeviceInfo.getUserAgent(),
        visitedAt: new Date().toISOString(),
      };

      AuthService.siteLocation({ data });
    }, 15000);
  },
});

startListening({
  matcher: isAnyOf(appInBackground, logout.fulfilled),
  effect: () => {
    clearHeartBeatInterval();
  },
});
